import { create } from "zustand";
import {
  collection,
  query,
  limit,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  onSnapshot,
  Unsubscribe,
  serverTimestamp,
  FieldValue,
} from "firebase/firestore";
import { db } from "@um/ui";
import { Mind, SupportMember, withRef, WithRef } from "@um/contracts";
import { useAuthStore } from "@um/ui";
import { getCollection } from "@um/ui";
import { logException } from "@um/ui";
interface ProfilesState {
  profiles: WithRef<Mind>[];
  loading: boolean;
  error: string | null;
  subscribedAccountId: string | null; // Track which account we're subscribed to

  // Actions
  fetchProfiles: () => Promise<void>;
  createProfile: (
    profile: Partial<Mind>,
    hasOwnDevice?: boolean
  ) => Promise<string>;
  /**
   * One-shot lookup of a Mind in the account that the CURRENT user could
   * reasonably claim as their own — i.e. not deleted, and not already linked
   * to another user's role.
   *
   * Use before creating a "self" profile: `userMind` is derived solely from
   * `userRole.mind`, so it reads null whenever that link is missing even
   * though the Mind exists, which is how duplicate Minds got created
   * (UM-289). Returns null when nothing is safely adoptable, in which case
   * the caller should create a Mind as before.
   */
  findAdoptableSelfMindId: (accountId?: string) => Promise<string | null>;
  updateProfile: (id: string, profile: Partial<Mind>) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;

  // Subscription - now takes accountId as parameter
  subscribeToProfiles: (accountId?: string) => Unsubscribe | null;
  unsubscribeFromProfiles: () => void;
  setProfiles: (profiles: WithRef<Mind>[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useProfilesStore = create<ProfilesState>((set, get) => {
  let profilesUnsubscribe: Unsubscribe | null = null;

  return {
    profiles: [],
    loading: false,
    error: null,
    subscribedAccountId: null,

    setProfiles: (profiles) => set({ profiles }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),

    fetchProfiles: async () => {
      const { accountId } = useAuthStore.getState();
      if (!accountId) {
        set({ error: "No account ID available" });
        return;
      }

      const unsubscribe = get().subscribeToProfiles(accountId);

      if (unsubscribe) {
        profilesUnsubscribe = unsubscribe;
      }
    },

    createProfile: async (profile, hasOwnDevice) => {
      const { accountId, account } = useAuthStore.getState();
      if (!accountId) {
        throw new Error("No account ID available");
      }

      set({ loading: true, error: null });

      try {
        const profilesRef = collection(db, `Account/${accountId}/Mind`);
        const newProfileRef = doc(profilesRef);

        const timestamp = serverTimestamp();
        const profileData = {
          ...profile,
          isDeleted: false,
          isHidden: false,
          personas: profile.personas || [
            {
              id: "default",
              name: profile.name?.split(" ")[0] || "User",
            },
          ],
          // there is no UI to specify this yet so we apply the policy of all account owners
          network: account?.userRoles
            ?.filter((role) => role.role === "owner")
            .map(
              (role) =>
                ({
                  agent: role.agent,
                  notifications: {
                    toMind: !hasOwnDevice,
                    toCaregiver: true,
                  },
                }) as SupportMember
            ),

          status: profile.status || "In Progress",
          created: timestamp,
          updated: timestamp,
        };

        await setDoc(newProfileRef, profileData);

        set({ loading: false });
        return newProfileRef.id;
      } catch (error) {
        logException(error);
        console.error("Error creating profile:", error);
        set({
          error:
            error instanceof Error
              ? error.message
              : "Unknown error creating profile",
          loading: false,
        });
        throw error;
      }
    },

    findAdoptableSelfMindId: async (providedAccountId?: string) => {
      const authState = useAuthStore.getState();
      const accountId = providedAccountId || authState.accountId;
      if (!accountId) return null;

      // We need the account's userRoles to know which Minds belong to other
      // people. An account routinely holds Minds for the people it supports,
      // and adopting one of those as the current user's own profile would be
      // worse than creating a duplicate. If the loaded account isn't the one
      // being asked about, we can't make that judgement — decline.
      const account = authState.account;
      if (!account || account.ref.id !== accountId) return null;

      const currentUid = authState.user?.uid;
      const claimedByOthers = new Set(
        (account.userRoles ?? [])
          .filter((role) => role.mind && role.agent?.user?.id !== currentUid)
          .map((role) => role.mind!.id)
      );

      const isAdoptable = (id: string, isDeleted?: boolean) =>
        isDeleted !== true && !claimedByOthers.has(id);

      // Prefer anything already streamed in by the subscription.
      if (get().subscribedAccountId === accountId) {
        const cached = get().profiles.find((p) =>
          isAdoptable(p.ref.id, p.isDeleted)
        );
        if (cached) return cached.ref.id;
      }

      try {
        // Fetch a small page unfiltered rather than using where("isDeleted",
        // "==", false): older Mind docs predate that flag and a where-filter
        // would silently skip them.
        const snap = await getDocs(
          query(collection(db, `Account/${accountId}/Mind`), limit(20))
        );
        const match = snap.docs.find((d) =>
          isAdoptable(d.id, (d.data() as Mind).isDeleted)
        );
        return match ? match.id : null;
      } catch (error) {
        logException(error);
        console.error("Error looking up an adoptable profile:", error);
        // Fail closed: the caller creates a Mind, i.e. the previous behavior.
        return null;
      }
    },

    updateProfile: async (id, profile) => {
      const { accountId } = useAuthStore.getState();
      if (!accountId) {
        throw new Error("No account ID available");
      }

      set({ loading: true, error: null });

      try {
        const profileRef = doc(db, `Account/${accountId}/Mind/${id}`);

        if (profile.status === "Complete") {
          const snap = await getDoc(profileRef);
          const existing = snap.data() as Mind | undefined;
          if (existing?.status !== "Complete") {
            // Points for completed profile are added by the backend
          }
        }

        const updateData = {
          ...profile,
          updated: serverTimestamp(),
        };

        await updateDoc(profileRef, updateData);

        // Let the realtime subscription handle the state update
        set({ loading: false });
      } catch (error) {
        logException(error);
        console.error("Error updating profile:", error);
        set({
          error:
            error instanceof Error
              ? error.message
              : "Unknown error updating profile",
          loading: false,
        });
        throw error;
      }
    },

    deleteProfile: async (id) => {
      const { accountId } = useAuthStore.getState();
      if (!accountId) {
        throw new Error("No account ID available");
      }

      set({ loading: true, error: null });

      try {
        const profileRef = doc(db, `Account/${accountId}/Mind/${id}`);
        await updateDoc(profileRef, {
          isDeleted: true,
          updated: serverTimestamp(),
        });

        // Let the realtime subscription handle the state update
        set({ loading: false });
      } catch (error) {
        logException(error);
        console.error("Error deleting profile:", error);
        set({
          error:
            error instanceof Error
              ? error.message
              : "Unknown error deleting profile",
          loading: false,
        });
        throw error;
      }
    },

    subscribeToProfiles: (providedAccountId?: string) => {
      // Use provided accountId or fall back to store state
      const accountId = providedAccountId || useAuthStore.getState().accountId;
      if (!accountId) {
        console.log(
          "[ProfilesStore] No account ID available, skipping subscription"
        );
        set({ error: "No account ID available", loading: false, profiles: [] });
        return null;
      }

      // If already subscribed to this account and not loading, don't re-subscribe
      const currentSubscribedId = get().subscribedAccountId;
      const isCurrentlyLoading = get().loading;
      if (
        currentSubscribedId === accountId &&
        profilesUnsubscribe &&
        !isCurrentlyLoading
      ) {
        console.log(
          "[ProfilesStore] Already subscribed to account:",
          accountId
        );
        return profilesUnsubscribe;
      }

      // Unsubscribe from previous subscription if exists
      if (profilesUnsubscribe) {
        console.log(
          "[ProfilesStore] Unsubscribing from previous account:",
          currentSubscribedId
        );
        profilesUnsubscribe();
        profilesUnsubscribe = null;
      }

      console.log(
        "[ProfilesStore] Subscribing to profiles for account:",
        accountId
      );
      set({
        loading: true,
        error: null,
        subscribedAccountId: accountId,
        profiles: [],
      });

      // Add a timeout to prevent infinite loading state
      const loadingTimeout = setTimeout(() => {
        const state = get();
        if (state.loading && state.subscribedAccountId === accountId) {
          console.warn(
            "[ProfilesStore] Subscription timeout - setting loading to false"
          );
          set({ loading: false });
        }
      }, 10000); // 10 second timeout

      const profilesRef = getCollection<Mind>(`Account/${accountId}/Mind`);
      // Use simpler query without orderBy to avoid index requirements
      // Sort in memory instead
      const q = query(profilesRef);

      console.log(
        "[ProfilesStore] Starting onSnapshot listener for:",
        `Account/${accountId}/Mind`
      );

      try {
        profilesUnsubscribe = onSnapshot(
          q,
          (snapshot) => {
            clearTimeout(loadingTimeout);
            console.log(
              "[ProfilesStore] onSnapshot fired, docs count:",
              snapshot.docs.length
            );

            const profiles: WithRef<Mind>[] = snapshot.docs
              .map(withRef)
              .filter((profile) => !profile.isDeleted)
              // Sort by created date descending (in memory)
              .sort((a, b) => {
                const aCreated = a.created?.toMillis?.() || 0;
                const bCreated = b.created?.toMillis?.() || 0;
                return bCreated - aCreated;
              });

            console.log(
              "[ProfilesStore] Loaded profiles:",
              profiles.length,
              "for account:",
              accountId
            );
            set({ profiles, loading: false });
          },
          (error) => {
            clearTimeout(loadingTimeout);
            console.error(
              "[ProfilesStore] Error subscribing to profiles:",
              error
            );
            set({
              error:
                error instanceof Error
                  ? error.message
                  : "Unknown error subscribing to profiles",
              loading: false,
              profiles: [],
            });
          }
        );
      } catch (error) {
        clearTimeout(loadingTimeout);
        console.error("[ProfilesStore] Failed to create subscription:", error);
        set({
          loading: false,
          profiles: [],
          error: "Failed to subscribe to profiles",
        });
        return null;
      }

      return profilesUnsubscribe;
    },

    unsubscribeFromProfiles: () => {
      if (profilesUnsubscribe) {
        profilesUnsubscribe();
        profilesUnsubscribe = null;
      }
      set({ subscribedAccountId: null });
    },
  };
});
